import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

/**
 * Excel cell-annotation surface (T-0036 / E04-F05 — inline Excel rendering
 * with cell-level annotation).
 *
 * An Excel cell annotation IS an `EvidenceLink` (ADR-014 + E04-F03 §3) —
 * we do NOT introduce a parallel annotation store. The annotation is
 * encoded as:
 *
 *   {
 *     source_type:        'register_entry',        // any allowed source
 *     source_id:          <register_entry_id>,     // the cited entry
 *     target_document_id: <document_id>,           // the workbook
 *     target_kind:        'excel_cell',
 *     sheet_name:         '<sheet>',
 *     cell_address:       '<A1>',
 *   }
 *
 * The T-0034 evidence-links endpoint already accepts this shape — the
 * underlying schema's `evidence_links_granularity_xor_check` CHECK
 * constraint enforces that `target_kind='excel_cell'` rows carry
 * `sheet_name` + `cell_address` and NO `page`. No migration is required.
 *
 * The bidirectional query `?target_document_id=&sheet_name=&cell_address=`
 * exposed by T-0034 is what surfaces existing annotations on a workbook
 * when the viewer opens (used by `useExcelAnnotationsForDocumentQuery`).
 */

// ===========================================================================
// Schemas
// ===========================================================================

export const ExcelAnnotationItemSchema = z.object({
  id: z.string().uuid(),
  source_type: z.string(),
  source_id: z.string().uuid(),
  source_field_id: z.string().nullable(),
  target_kind: z.string(),
  page: z.number().nullable(),
  section_path: z.string().nullable(),
  sheet_name: z.string().nullable(),
  cell_address: z.string().nullable(),
  created_at: z.string(),
});
export type ExcelAnnotationItem = z.infer<typeof ExcelAnnotationItemSchema>;

export const ExcelAnnotationListSchema = z.object({
  items: z.array(ExcelAnnotationItemSchema),
});
export type ExcelAnnotationList = z.infer<typeof ExcelAnnotationListSchema>;

export const CreatedEvidenceLinkSchema = z
  .object({
    id: z.string().uuid(),
    target_kind: z.string(),
  })
  .passthrough();
export type CreatedEvidenceLink = z.infer<typeof CreatedEvidenceLinkSchema>;

// ===========================================================================
// Input types
// ===========================================================================

export interface CreateExcelAnnotationInput {
  /**
   * The register entry being cited by this cell. v1 only supports
   * `register_entry` as the source per the evidence-links service
   * (`ALLOWED_SOURCE_TYPES`).
   */
  source_register_entry_id: string;
  /** The workbook document. */
  target_document_id: string;
  /** Sheet name as it appears in the workbook (e.g. "Defects-P1"). */
  sheet_name: string;
  /** A1-style cell reference (e.g. "B47"). */
  cell_address: string;
  /**
   * Optional caller-provided field id surfaced on the source entry that
   * the annotation is anchored to — matches the existing
   * EvidenceLink.source_field_id contract.
   */
  source_field_id?: string;
}

// ===========================================================================
// Functions
// ===========================================================================

/**
 * Create an Excel cell annotation as an EvidenceLink.
 *
 * Calls the T-0034 endpoint POST `/api/v1/engagements/:id/evidence-links`
 * with `target_kind='excel_cell'`. Idempotency key is derived from the
 * (source_entry, document, sheet, cell) tuple so a retry of the same
 * annotation collapses cleanly.
 */
export function createExcelAnnotation(
  engagementId: string,
  input: CreateExcelAnnotationInput,
  idempotencyKey?: string,
): Promise<CreatedEvidenceLink> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/evidence-links`,
    CreatedEvidenceLinkSchema,
    {
      method: 'POST',
      body: {
        source_type: 'register_entry',
        source_id: input.source_register_entry_id,
        target_document_id: input.target_document_id,
        target_kind: 'excel_cell',
        sheet_name: input.sheet_name,
        cell_address: input.cell_address,
        ...(input.source_field_id ? { source_field_id: input.source_field_id } : {}),
      },
      ...(idempotencyKey ? { idempotencyKey } : {}),
    },
  );
}

/**
 * List existing annotations on a workbook. Optionally filter to a single
 * sheet+cell when the viewer wants to render the "what cites this cell"
 * panel; without filters, lists every cell-level annotation on the doc.
 */
export function listExcelAnnotationsForDocument(
  engagementId: string,
  documentId: string,
  opts?: { sheetName?: string; cellAddress?: string },
): Promise<ExcelAnnotationList> {
  // The evidence-links GET handler requires either source OR target params.
  // For a workbook-wide list we still need a granularity hint (page OR
  // sheet+cell); the API rejects an unqualified target query. v1 wires
  // the call only when both sheet+cell are present (cell-level query);
  // a workbook-wide list is left for a follow-up that builds an
  // aggregated index of annotated cells.
  if (!opts || !opts.sheetName || !opts.cellAddress) {
    return Promise.resolve({ items: [] });
  }
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/evidence-links`,
    ExcelAnnotationListSchema,
    {
      query: {
        target_document_id: documentId,
        sheet_name: opts.sheetName,
        cell_address: opts.cellAddress,
      },
    },
  );
}

// ===========================================================================
// TanStack Query hooks
// ===========================================================================

/**
 * Mutation hook for creating a cell annotation. The on-success path
 * invalidates the cell-scoped annotations query so the workbook view
 * refreshes its "annotated cell" markers immediately.
 *
 * Curried on (engagementId, documentId, sheetName, cellAddress) per the
 * ticket spec: in practice the viewer captures (sheet, cell) at the
 * click site and binds the mutation in the modal.
 */
export function useCreateExcelAnnotationMutation(
  engagementId: string | undefined,
  documentId: string | undefined,
  sheetName: string | undefined,
  cellA1: string | undefined,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      source_register_entry_id: string;
      source_field_id?: string;
      idempotencyKey?: string;
    }) => {
      if (!engagementId || !documentId || !sheetName || !cellA1) {
        return Promise.reject(
          new Error(
            'engagementId, documentId, sheetName, and cellA1 are required to create an excel annotation.',
          ),
        );
      }
      return createExcelAnnotation(
        engagementId,
        {
          source_register_entry_id: vars.source_register_entry_id,
          target_document_id: documentId,
          sheet_name: sheetName,
          cell_address: cellA1,
          ...(vars.source_field_id ? { source_field_id: vars.source_field_id } : {}),
        },
        vars.idempotencyKey,
      );
    },
    onSuccess: () => {
      if (engagementId && documentId && sheetName && cellA1) {
        void qc.invalidateQueries({
          queryKey: ['excel-annotations', engagementId, documentId, sheetName, cellA1] as const,
        });
      }
    },
  });
}

/**
 * Query hook listing the annotations on a specific (sheet, cell). The
 * viewer renders an "annotated" badge on cells with any non-empty result.
 */
export function useExcelAnnotationsForCellQuery(
  engagementId: string | undefined,
  documentId: string | undefined,
  sheetName: string | undefined,
  cellAddress: string | undefined,
) {
  return useQuery({
    queryKey: ['excel-annotations', engagementId, documentId, sheetName, cellAddress] as const,
    queryFn: () =>
      listExcelAnnotationsForDocument(engagementId!, documentId!, {
        sheetName: sheetName!,
        cellAddress: cellAddress!,
      }),
    enabled: Boolean(engagementId && documentId && sheetName && cellAddress),
  });
}
